import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, forkJoin, map, Observable, switchMap, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  AddCustomListRequestDto,
  AddCustomListValueRequestDto,
  CustomListsService as ApiCustomListsService,
  EditCustomListRequestDto,
  EditCustomListValueRequestDto,
  GetCustomListsNamesResponseDto,
  GetCustomListsValuesResponseDto,
  Id,
} from '@src/api';
import { AlertService } from '@src/core/services';

import { ContactsCustomListItem } from '../../contacts-directory';

@Injectable({
  providedIn: 'root',
})
export class CustomListService {
  private readonly apiCustomListsService = inject(ApiCustomListsService);
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);

  customListDirectories$ = new BehaviorSubject<ContactsCustomListItem[] | null>(null);
  customListValues$ = new BehaviorSubject<ContactsCustomListItem[] | null>(null);

  getCustomListDirectories() {
    this.customListDirectories$.next(null);

    this.apiCustomListsService
      .getCustomListsNames()
      .pipe(
        catchError(err => {
          this.clearAllData();

          this.alertService.error(err);

          return throwError(() => err);
        }),
      )
      .subscribe(data => {
        this.customListDirectories$.next(this.formatCustomListResponseForUI(data));
      });
  }

  saveDirectories(newValues: ContactsCustomListItem[]) {
    const customList: Record<string, ContactsCustomListItem> = {};
    newValues.forEach((value, index) => {
      customList[index] = value;
    });
    const requestsList: Record<string, Observable<Id>> = {};

    Object.entries(customList).forEach(([key, value]) => {
      if (!value.id) {
        requestsList[key] = this.addCustomListDirectoryObservable(this.formatDirectoriesForRequest([value])[0]);
      }

      const previousValue = this.customListDirectories$.value?.find(({ id }) => id && id === value.id);

      if (previousValue && previousValue.title !== value.title) {
        requestsList[key] = this.editCustomListDirectoryObservable(this.formatDirectoriesForRequest([value])[0]);
      }
    });

    if (Object.keys(requestsList).length) {
      return forkJoin(requestsList).pipe(
        switchMap(res => {
          const array = Object.entries(res);

          for (let index = 0; index < array.length; index++) {
            const [key, value] = array[index];

            if (!value.id) {
              this.alertService.error(
                this.translateService.instant('components.customList.alerts.errors.directoryAddOrUpdate'),
              );
              return throwError(() => value);
            }

            customList[key].id = value.id;
          }

          return this.updateDirectorySortOrder(customList);
        }),
      );
    }

    return this.updateDirectorySortOrder(customList);
  }

  updateDirectorySortOrder(list: Record<string, ContactsCustomListItem>) {
    const valueIds: string[] = Object.values(list).map(({ id }) => id) as string[];

    return this.apiCustomListsService.updateCustomListsSortOrder({ valueIds });
  }

  saveValues(newValues: ContactsCustomListItem[], customListId: string) {
    const customList: Record<string, ContactsCustomListItem> = {};
    newValues.forEach((value, index) => {
      customList[index] = value;
    });
    const requestsList: Record<string, Observable<Id>> = {};

    Object.entries(customList).forEach(([key, value]) => {
      if (!value.id) {
        requestsList[key] = this.addCustomListValueObservable(this.formatValuesForRequest([value], customListId)[0]);
      }

      const previousValue = this.customListValues$.value?.find(({ id }) => id && id === value.id);

      if (previousValue && previousValue.title !== value.title) {
        requestsList[key] = this.editCustomListValueObservable(this.formatValuesForRequest([value], customListId)[0]);
      }
    });

    if (Object.keys(requestsList).length) {
      return forkJoin(requestsList).pipe(
        switchMap(res => {
          const array = Object.entries(res);

          for (let index = 0; index < array.length; index++) {
            const [key, value] = array[index];

            if (!value.id) {
              this.alertService.error(
                this.translateService.instant('components.customList.alerts.errors.valueAddOrUpdate'),
              );
              return throwError(() => value);
            }

            customList[key].id = value.id;
          }

          return this.updateValueSortOrder(customList, customListId);
        }),
      );
    }

    return this.updateValueSortOrder(customList, customListId);
  }

  updateValueSortOrder(list: Record<string, ContactsCustomListItem>, customListId: string) {
    const valueIds: string[] = Object.values(list).map(({ id }) => id) as string[];

    return this.apiCustomListsService.updateValuesSortOrder({ valueIds, customListId });
  }

  deleteCustomListDirectoryObservable(directoryId: string) {
    return this.apiCustomListsService.deleteCustomList({ id: directoryId }).pipe(
      catchError(err => {
        this.alertService.error(err);

        return throwError(() => err);
      }),
    );
  }

  getCustomListValues(directoryId: string) {
    this.customListValues$.next(null);

    this.getCustomListValuesObservable(directoryId).subscribe(data => {
      this.customListValues$.next(data);
    });
  }

  getCustomListValuesObservable(directoryId: string) {
    return this.apiCustomListsService.getCustomListValues(directoryId).pipe(
      catchError(err => {
        this.alertService.error(err);

        return throwError(() => err);
      }),
      map(data => {
        return this.formatCustomListResponseForUI(data);
      }),
    );
  }

  deleteCustomListValueObservable(valueId: string) {
    return this.apiCustomListsService.deleteCustomListValue({ id: valueId }).pipe(
      catchError(err => {
        this.alertService.error(err);

        return throwError(() => err);
      }),
    );
  }

  addCustomListDirectoryObservable(directory: AddCustomListRequestDto): Observable<Id> {
    return this.apiCustomListsService?.addCustomList(directory)?.pipe(
      catchError(err => {
        this.alertService.error(err);

        return throwError(() => err);
      }),
    );
  }

  editCustomListDirectoryObservable(directory: EditCustomListRequestDto): Observable<Id> {
    return this.apiCustomListsService.editCustomList(directory).pipe(
      catchError(err => {
        this.alertService.error(err);

        return throwError(() => err);
      }),
    );
  }

  addCustomListValueObservable(value: AddCustomListValueRequestDto): Observable<Id> {
    return this.apiCustomListsService.addCustomListValue(value).pipe(
      catchError(err => {
        this.alertService.error(err);

        return throwError(() => err);
      }),
    );
  }

  editCustomListValueObservable(value: EditCustomListValueRequestDto): Observable<Id> {
    return this.apiCustomListsService.editCustomListValue(value).pipe(
      catchError(err => {
        this.alertService.error(err);

        return throwError(() => err);
      }),
    );
  }

  resetCustomListValues() {
    this.customListValues$.next(null);
  }

  private clearAllData() {
    this.customListDirectories$.next(null);
    this.customListValues$.next(null);
  }

  private formatCustomListResponseForUI(items: GetCustomListsNamesResponseDto[] | GetCustomListsValuesResponseDto[]) {
    return items.map((item, index) => {
      const title = 'value' in item ? item.value : 'name' in item ? item.name : '';

      return <ContactsCustomListItem>{
        id: item.id,
        title,
        sortOrder: index * 10,
      };
    });
  }

  private formatDirectoriesForRequest(items: ContactsCustomListItem[]) {
    return items.map(item => {
      return <EditCustomListRequestDto>{
        id: item.id,
        name: item.title,
        addedByAnyUser: false,
        sortOrder: item.sortOrder,
      };
    });
  }

  private formatValuesForRequest(items: ContactsCustomListItem[], customListId: string) {
    return items.map(item => {
      return <EditCustomListValueRequestDto>{
        id: item.id,
        customListId: customListId,
        value: item.title,
        sortOrder: item.sortOrder,
      };
    });
  }
}
